import type {
  Deposit,
  DepositSort,
  PaymentsAPIResponse
} from '@library/types/payments';
import {
  getPaymentsDateTime,
  isTimeWithinInterval
} from '@library/utils/dateTime';
import { DateTime } from 'luxon';
import { DepositsDetailModal } from '@library/components/modals/DepositsDetailModal/DepositsDetailModal';
import type { DepositsFilters } from '@library/types/filters';
import { DepositsHeader } from '@library/components/deposits/DepositsHeader';
import { DepositsRefundConfirmationModal } from '@library/components/modals/DepositsRefundConfirmationModal/DepositsRefundConfirmationModal';
import { DepositsRefundStatusModal } from '@library/components/modals/DepositsRefundStatusModal/DepositsRefundStatusModal';
import { DepositsRows } from '@bo-components/deposits/DepositsRows';
import { PaginationButtons } from '@library/components/buttons';
import { SELECT_ALL } from '@library/component-utils/filters';
import { useDepositRefunds } from '@bo-hooks/deposits/useDepositRefunds';
import { useState } from 'react';

export interface DepositsTableProps {
  data: PaymentsAPIResponse;
  dataRefetch?: () => void;
  filters: DepositsFilters;
  fromDate: string;
  toDate: string;
}

export const DepositsTable = ({
  data,
  dataRefetch = () => {},
  filters,
  fromDate,
  toDate
}: DepositsTableProps) => {
  const [sort, setSort] = useState<DepositSort>({
    sortBy: 'created',
    sortDirection: 'asc'
  });

  const [currentPage, setCurrentPage] = useState(1);
  const [refundInitiated, setRefundInitiated] = useState(false);

  const [selectedDeposit, setSelectedDeposit] = useState<Deposit | undefined>(
    undefined
  );

  const handleDepositRowClick = (
    _event: React.MouseEvent<HTMLInputElement | HTMLTableRowElement>,
    deposits: Deposit
  ) => {
    setSelectedDeposit(deposits);
  };

  const {
    submitDepositsForRefund,
    refundStatuses,
    refundsComplete,
    resetState
  } = useDepositRefunds(selectedDeposit ? [selectedDeposit] : []);

  const onCancelModalCallback = () => {
    setSelectedDeposit(undefined);
  };

  const handleRefundInitiated = () => {
    setRefundInitiated(true);
  };

  let filteredDeposits = data.deposits;

  filteredDeposits = filteredDeposits.filter((deposit) =>
    isTimeWithinInterval({
      time: getPaymentsDateTime(deposit.created),
      start: DateTime.fromISO(fromDate).startOf('day'),
      end: DateTime.fromISO(toDate).endOf('day')
    })
  );

  if (filters.depositStatus !== SELECT_ALL) {
    filteredDeposits = filteredDeposits.filter(
      (deposit) => deposit.status === filters.depositStatus
    );
  }

  if (filters.depositMethodType !== SELECT_ALL) {
    filteredDeposits = filteredDeposits.filter(
      (deposit) => deposit.method === filters.depositMethodType
    );
  }

  const [refundError, setRefundError] = useState<string | undefined>(undefined);

  const onCancelRefund = () => {
    setRefundInitiated(false);
    setRefundError(undefined);
    resetState();
  };

  const handleStatusModalClose = () => {
    // refetch the data
    dataRefetch();

    // clear the state of refund modal
    onCancelRefund();
  };

  return (
    <>
      {!refundInitiated && (
        <DepositsDetailModal
          user={data.user}
          deposit={selectedDeposit}
          onCancelModal={onCancelModalCallback}
          onRefundInitiated={handleRefundInitiated}
        />
      )}
      {refundInitiated && selectedDeposit && !refundsComplete && (
        <DepositsRefundConfirmationModal
          deposits={[selectedDeposit]}
          onCancelModal={onCancelRefund}
          onConfirm={() => submitDepositsForRefund()}
          error={refundError}
        />
      )}
      {refundInitiated && selectedDeposit && refundsComplete && (
        <DepositsRefundStatusModal
          deposits={[selectedDeposit]}
          onCancelModal={handleStatusModalClose}
          statuses={refundStatuses}
        />
      )}
      <table className="w-full">
        <DepositsHeader handleSort={setSort} sort={sort} />
        <DepositsRows
          onDepositRowClick={handleDepositRowClick}
          deposits={filteredDeposits}
          user={data.user}
        />
      </table>

      <PaginationButtons
        currentPage={currentPage}
        handlePageChange={setCurrentPage}
        totalPages={data?.paginationInfo.total_pages ?? 0}
      />
    </>
  );
};
